import React, { useEffect, useState } from 'react';
import { Activity, Cable, RefreshCw, ShieldCheck, Workflow, Database, NotebookText, Wrench, KeyRound, Copy, Sparkles, CalendarClock, TriangleAlert, CloudCog } from 'lucide-react';

import { api } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';


const statusClassMap = {
  connected: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/20',
  syncing: 'bg-cyan-500/10 text-cyan-200 border-cyan-400/20',
  warning: 'bg-amber-500/10 text-amber-200 border-amber-400/20',
  error: 'bg-red-500/10 text-red-200 border-red-400/20',
  disconnected: 'bg-white/10 text-white/60 border-white/10',
};


export default function ConnectionsPage() {
  const [connectors, setConnectors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [canonical, setCanonical] = useState(null);
  const [routing, setRouting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const [logsDialog, setLogsDialog] = useState({ open: false, title: '', logs: [] });
  const [editDialog, setEditDialog] = useState({ open: false, slug: '', name: '', sync_frequency_minutes: 60, admin_notes: '', advanced_webhook_url: '', advanced_routing_notes: '' });
  const [generatedKeyDialog, setGeneratedKeyDialog] = useState({ open: false, name: '', raw_key: '', scopes: [] });

  const ecosystemBaseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ecosystem`;

  const loadData = async () => {
    setLoading(true);
    try {
      const [connectorsRes, summaryRes, canonicalRes, routingRes] = await Promise.all([
        api.get('/integrations/connectors'),
        api.get('/integrations/connectors/summary'),
        api.get('/integrations/canonical/summary'),
        api.get('/integrations/routing/summary'),
      ]);
      setConnectors(connectorsRes.data);
      setSummary(summaryRes.data);
      setCanonical(canonicalRes.data);
      setRouting(routingRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load connections control center');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const runConnectorAction = async (slug, action, successMessage) => {
    setActiveSlug(`${slug}:${action}`);
    try {
      const response = await api.post(`/integrations/connectors/${slug}/${action}`);
      toast.success(successMessage || response.data.message || 'Action complete');
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActiveSlug(null);
    }
  };

  const openLogs = async (connector) => {
    try {
      const response = await api.get(`/integrations/connectors/${connector.slug}/logs`);
      setLogsDialog({ open: true, title: connector.name, logs: response.data });
    } catch (error) {
      console.error(error);
      toast.error('Failed to load diagnostics logs');
    }
  };

  const openEdit = (connector) => {
    setEditDialog({
      open: true,
      slug: connector.slug,
      name: connector.name,
      sync_frequency_minutes: connector.sync_frequency_minutes || 60,
      admin_notes: connector.admin_notes || '',
      advanced_webhook_url: connector.advanced_webhook_url || '',
      advanced_routing_notes: connector.advanced_routing_notes || '',
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/integrations/connectors/${editDialog.slug}`, {
        sync_frequency_minutes: Number(editDialog.sync_frequency_minutes),
        admin_notes: editDialog.admin_notes,
        advanced_webhook_url: editDialog.advanced_webhook_url,
        advanced_routing_notes: editDialog.advanced_routing_notes,
      });
      toast.success(`Updated ${editDialog.name}`);
      setEditDialog({ open: false, slug: '', name: '', sync_frequency_minutes: 60, admin_notes: '' });
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update connector settings');
    }
  };

  const generateProviderKey = async (connector) => {
    setActiveSlug(`${connector.slug}:provider-key`);
    try {
      const response = await api.post(`/integrations/connectors/${connector.slug}/provider-key`);
      setGeneratedKeyDialog({ open: true, name: connector.name, raw_key: response.data.raw_key, scopes: response.data.scopes || [] });
      toast.success(`${connector.name} key generated`);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to generate provider key');
    } finally {
      setActiveSlug(null);
    }
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (error) {
      console.error(error);
      toast.error(`Failed to copy ${label}`);
    }
  };

  const topEntityCounts = canonical?.counts ? Object.entries(canonical.counts).filter(([, count]) => count > 0).slice(0, 6) : [];

  return (
    <div className="h-full overflow-auto p-6 md:p-10 max-w-7xl mx-auto space-y-8 rounded-3xl lg-glass" data-testid="connections-page">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight text-white">Connections Control Center</h1>
        <p className="text-white/50 max-w-4xl">AtCoastal.AI is the admin-managed backend intelligence hub for the AtCoastal ecosystem. Use this space to manage live connectors, canonical records, and query-routing behavior.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card className="lg-glass-strong border-white/10 text-white" data-testid="connections-summary-card">
          <CardContent className="p-5 space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] text-white/35">Connectors</div>
            <div className="text-3xl font-semibold">{summary?.connectors_total ?? (loading ? '…' : 0)}</div>
          </CardContent>
        </Card>
        <Card className="lg-glass-strong border-white/10 text-white" data-testid="canonical-summary-card">
          <CardContent className="p-5 space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] text-white/35">Canonical Records</div>
            <div className="text-3xl font-semibold">{summary?.canonical_total ?? (loading ? '…' : 0)}</div>
          </CardContent>
        </Card>
        <Card className="lg-glass-strong border-white/10 text-white" data-testid="routing-summary-card">
          <CardContent className="p-5 space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] text-white/35">Routing Categories</div>
            <div className="text-3xl font-semibold">{summary?.routing_categories ?? (loading ? '…' : 0)}</div>
          </CardContent>
        </Card>
        <Card className="lg-glass-strong border-white/10 text-white" data-testid="connector-status-summary-card">
          <CardContent className="p-5 space-y-2">
            <div className="text-xs uppercase tracking-[0.2em] text-white/35">Connected Now</div>
            <div className="text-3xl font-semibold">{summary?.status_counts?.connected ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="lg-glass-strong border-white/10 text-white" data-testid="connections-grid-card">
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Cable className="h-5 w-5 text-cyan-400" /> Ecosystem Connectors</CardTitle>
            <CardDescription className="text-white/45">Real connection first for AtCoastal.Cloud, with placeholders ready for Signal Tools, Ratified, and Zapier/BoomTown.</CardDescription>
          </div>
          <Button variant="outline" onClick={loadData} className="border-white/10 bg-white/5 text-white hover:bg-white/10" data-testid="refresh-connections-button">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {connectors.map((connector) => {
            const actionKey = (action) => activeSlug === `${connector.slug}:${action}`;
            const statusClass = statusClassMap[connector.status] || statusClassMap.disconnected;
            const isProviderConnector = ['signal_tools', 'ratified'].includes(connector.slug);
            const providerKeyStatus = connector.provider_key_status;
            return (
              <div key={connector.slug} className="rounded-3xl border border-white/10 bg-black/20 p-5 space-y-4" data-testid={`connector-card-${connector.slug}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-lg font-semibold text-white">{connector.name}</div>
                      <Badge variant="outline" className={statusClass} data-testid={`connector-status-${connector.slug}`}>{connector.status}</Badge>
                    </div>
                    <div className="mt-1 text-xs text-white/45 break-all">{connector.base_url || 'Waiting for endpoint'}</div>
                  </div>
                  <Badge variant="secondary" className="uppercase text-[10px] tracking-wider">{connector.environment_mode}</Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs text-white/65">
                  <div><span className="text-white/35">Sync:</span> {connector.sync_strategy}</div>
                  <div><span className="text-white/35">Frequency:</span> {connector.sync_frequency_minutes} min</div>
                  <div><span className="text-white/35">Auth:</span> {connector.auth_mode}</div>
                  <div><span className="text-white/35">Webhooks:</span> {connector.supports_webhooks ? 'Supported' : 'Not yet'}</div>
                  <div className="col-span-2"><span className="text-white/35">Last Sync:</span> {connector.last_sync_at ? new Date(connector.last_sync_at).toLocaleString() : 'Never'}</div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3 text-xs leading-relaxed text-white/70" data-testid={`connector-notes-${connector.slug}`}>
                  <div><span className="text-white/35">Notes:</span> {connector.notes}</div>
                  {connector.admin_notes && <div className="mt-2"><span className="text-white/35">Admin:</span> {connector.admin_notes}</div>}
                </div>

                {isProviderConnector && (
                  <div className="rounded-2xl border border-cyan-500/15 bg-cyan-500/5 p-4 space-y-3" data-testid={`provider-setup-${connector.slug}`}>
                    <div className="flex items-center gap-2 text-sm font-medium text-cyan-100"><CloudCog className="h-4 w-4" /> App-side Provider Setup</div>
                    <div className="space-y-2 text-xs text-white/65">
                      <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                        <div className="text-white/35 mb-1">API Base URL</div>
                        <div className="break-all text-cyan-100">{ecosystemBaseUrl}</div>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                        <div className="text-white/35 mb-1">{connector.slug === 'signal_tools' ? 'Signal Key' : 'API Key'}</div>
                        <div>{providerKeyStatus?.key_prefix ? `${providerKeyStatus.key_prefix}••••••` : 'Not generated yet'}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 text-xs text-cyan-100/90">
                      <div className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Transaction intelligence summary</div>
                      <div className="flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5" /> Timelines & deadlines</div>
                      <div className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" /> Scoped connector keys</div>
                      <div className="flex items-center gap-2"><TriangleAlert className="h-3.5 w-3.5" /> Future webhooks/routing ready</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => copyText(ecosystemBaseUrl, 'API base URL')} className="border-white/10 bg-white/5 text-white hover:bg-white/10" data-testid={`copy-provider-url-${connector.slug}`}>
                        <Copy className="mr-2 h-3.5 w-3.5" /> Copy URL
                      </Button>
                      <Button type="button" size="sm" onClick={() => generateProviderKey(connector)} disabled={actionKey('provider-key')} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid={`generate-provider-key-${connector.slug}`}>
                        <KeyRound className="mr-2 h-3.5 w-3.5" /> {providerKeyStatus ? 'Regenerate Key' : 'Generate Key'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => runConnectorAction(connector.slug, 'test', `${connector.name} test complete`)} disabled={actionKey('test')} className="border-white/10 bg-white/5 text-white hover:bg-white/10" data-testid={`test-connector-${connector.slug}`}>
                    <Activity className="mr-2 h-3.5 w-3.5" /> {actionKey('test') ? 'Testing...' : 'Test'}
                  </Button>
                  <Button type="button" size="sm" onClick={() => runConnectorAction(connector.slug, 'sync', `${connector.name} sync started`)} disabled={actionKey('sync')} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid={`sync-connector-${connector.slug}`}>
                    <Workflow className="mr-2 h-3.5 w-3.5" /> {actionKey('sync') ? 'Syncing...' : 'Sync Now'}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => runConnectorAction(connector.slug, 'reconnect', `${connector.name} reconnect complete`)} className="text-white/70 hover:bg-white/10" data-testid={`reconnect-connector-${connector.slug}`}>
                    Reconnect
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => openLogs(connector)} className="text-white/70 hover:bg-white/10" data-testid={`logs-connector-${connector.slug}`}>
                    Diagnostics
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(connector)} className="text-white/70 hover:bg-white/10" data-testid={`edit-connector-${connector.slug}`}>
                    <Wrench className="mr-2 h-3.5 w-3.5" /> Settings
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Card className="lg-glass-strong border-white/10 text-white" data-testid="canonical-model-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Database className="h-5 w-5 text-cyan-400" /> Canonical Data Model</CardTitle>
            <CardDescription className="text-white/45">Normalized entities and mapping counts decouple AtCoastal.AI from the raw schemas of connected systems.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {topEntityCounts.map(([entity, count]) => (
                <Badge key={entity} variant="outline" className="border-cyan-500/20 bg-cyan-500/10 text-cyan-200" data-testid={`canonical-entity-${entity}`}>
                  {entity}: {count}
                </Badge>
              ))}
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-xs text-white/65" data-testid="canonical-mappings-summary">
              External ID mappings stored: {canonical?.mappings_count ?? 0}
            </div>
          </CardContent>
        </Card>

        <Card className="lg-glass-strong border-white/10 text-white" data-testid="routing-skeleton-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><NotebookText className="h-5 w-5 text-cyan-400" /> Query Routing Skeleton</CardTitle>
            <CardDescription className="text-white/45">Foundational routing rules decide whether a question should use knowledge, live data, document parsing, or hybrid orchestration.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(routing?.categories || []).map((category) => (
              <div key={category.category} className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid={`routing-category-${category.category}`}>
                <div className="font-medium text-white/95">{category.category}</div>
                <div className="mt-1 text-xs text-white/55">{category.description}</div>
                <div className="mt-2 text-[11px] text-cyan-200">Sources: {category.default_sources.join(' → ')}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={logsDialog.open} onOpenChange={(open) => setLogsDialog((current) => ({ ...current, open }))}>
        <DialogContent className="lg-glass-strong border-white/10 text-white max-w-3xl" data-testid="connector-logs-dialog">
          <DialogHeader>
            <DialogTitle>{logsDialog.title} Diagnostics</DialogTitle>
            <DialogDescription className="text-white/45">Latest connector events, retries, and sync diagnostics.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto space-y-3 py-2">
            {logsDialog.logs.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-6 text-sm text-white/45">No logs yet.</div>
            ) : logsDialog.logs.map((log, index) => (
              <div key={`${log.timestamp}-${index}`} className="rounded-2xl border border-white/10 bg-black/20 p-4 text-xs space-y-2" data-testid="connector-log-entry">
                <div className="flex items-center justify-between gap-3">
                  <Badge variant="outline" className={statusClassMap[log.status] || statusClassMap.disconnected}>{log.status}</Badge>
                  <span className="text-white/35">{new Date(log.timestamp).toLocaleString()}</span>
                </div>
                <div className="text-white/80">{log.message}</div>
                <pre className="whitespace-pre-wrap break-all text-[11px] text-white/45">{JSON.stringify(log.details || {}, null, 2)}</pre>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog((current) => ({ ...current, open }))}>
        <DialogContent className="lg-glass-strong border-white/10 text-white" data-testid="connector-settings-dialog">
          <DialogHeader>
            <DialogTitle>{editDialog.name} Settings</DialogTitle>
            <DialogDescription className="text-white/45">Adjust sync cadence and admin notes for this connector.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-xs text-white/50">Sync Frequency (minutes)</Label>
              <Input type="number" min="5" value={editDialog.sync_frequency_minutes} onChange={(event) => setEditDialog((current) => ({ ...current, sync_frequency_minutes: event.target.value }))} className="bg-black/20 border-white/10 text-white" data-testid="connector-frequency-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-white/50">Admin Notes</Label>
              <Textarea value={editDialog.admin_notes} onChange={(event) => setEditDialog((current) => ({ ...current, admin_notes: event.target.value }))} className="min-h-[120px] bg-black/20 border-white/10 text-white" data-testid="connector-notes-input" />
            </div>
            <details className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-white/75" data-testid="connector-advanced-settings">
              <summary className="cursor-pointer select-none text-white/90">Advanced routing / webhook settings</summary>
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs text-white/50">Future Webhook URL</Label>
                  <Input value={editDialog.advanced_webhook_url} onChange={(event) => setEditDialog((current) => ({ ...current, advanced_webhook_url: event.target.value }))} className="bg-black/20 border-white/10 text-white" data-testid="connector-webhook-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-white/50">Advanced Routing Notes</Label>
                  <Textarea value={editDialog.advanced_routing_notes} onChange={(event) => setEditDialog((current) => ({ ...current, advanced_routing_notes: event.target.value }))} className="min-h-[90px] bg-black/20 border-white/10 text-white" data-testid="connector-routing-notes-input" />
                </div>
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setEditDialog((current) => ({ ...current, open: false }))}>Cancel</Button>
            <Button type="button" onClick={saveEdit} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="connector-settings-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={generatedKeyDialog.open} onOpenChange={(open) => setGeneratedKeyDialog((current) => ({ ...current, open }))}>
        <DialogContent className="lg-glass-strong border-white/10 text-white" data-testid="generated-provider-key-dialog">
          <DialogHeader>
            <DialogTitle>{generatedKeyDialog.name} Credential</DialogTitle>
            <DialogDescription className="text-white/45">Copy this key now. For security, AtCoastal.AI will not display the full key again after you close this dialog.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-xs break-all text-cyan-100" data-testid="generated-provider-key-value">{generatedKeyDialog.raw_key}</div>
            <div className="flex flex-wrap gap-2 text-xs text-white/65">
              {generatedKeyDialog.scopes.map((scope) => (
                <Badge key={scope} variant="outline" className="border-cyan-500/20 bg-cyan-500/10 text-cyan-200">{scope}</Badge>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setGeneratedKeyDialog((current) => ({ ...current, open: false }))}>Close</Button>
            <Button type="button" onClick={() => copyText(generatedKeyDialog.raw_key, 'provider key')} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="copy-generated-provider-key-button">
              <Copy className="mr-2 h-4 w-4" /> Copy Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}